/// <reference types="q" />

/// Imports of 3rd Party ///
import Q = require("q");
/// Imports of VSS ///
import Context = require("VSS/Context");
import Diag = require("VSS/Diag");
import Resources_Platform = require("VSS/Resources/VSS.Resources.Platform");
import Utils_Core = require("VSS/Utils/Core");
import Utils_String = require("VSS/Utils/String");
import VSS = require("VSS/VSS");

//
// Keep track of whether or not the document is being unloaded. Without this logic, when a user
// clicks a link to navigate to a new page, we will show an error due to cancelled ajax requests.
//
var unloading = false;
var unloadRequested = false;
var eventListeners: IVssAjaxEventListener[] = [];
var outstandingRequestActionIds: IDictionaryNumberTo<number> = {};
var globalRequestId: number = 0;

export interface JQueryAjaxResult {
  jqXHR: JQueryXHR;
  textStatus: string;
}

export interface JQueryAjaxSuccessResult extends JQueryAjaxResult {
  data: any;
}

export interface JQueryAjaxErrorResult extends JQueryAjaxResult {
  errorThrown: any;
}

/**
 * Custom DataTypes that can be used in addition to jQuery's default text, json, xml, etc. types.
 * This module provides custom ajaxTransports for these types
 */
export module CustomTransportDataTypes {
  /**
   * Raw binary data returned as an ArrayBuffer
   */
  export var Binary = "arraybuffer";
}

/**
 * Custom ajax transports
 */
module CustomTransports {
  var registered = false;

  /**
   * Ensure that the custom transports are registered
   */
  export function register() {
    if (!registered) {
      _doRegistration();
      registered = true;
    }
  }

  function _doRegistration() {
    (<any>$).ajaxTransport(
      CustomTransportDataTypes.Binary,
      function (options: any, originalOptions: any, jqXHR: JQueryXHR) {
        if (
          options.dataType === CustomTransportDataTypes.Binary &&
          options.async
        ) {
          return {
            send: function (headers, completeCallback) {
              var xhr = new XMLHttpRequest();

              xhr.addEventListener("load", function () {
                var result = {};
                result[CustomTransportDataTypes.Binary] = xhr.response;
                completeCallback(
                  xhr.status,
                  xhr.statusText,
                  result,
                  xhr.getAllResponseHeaders()
                );
              });

              xhr.open(options.type, options.url, true);
              xhr.responseType = "arraybuffer";

              for (var headerName in headers) {
                xhr.setRequestHeader(headerName, headers[headerName]);
              }

              xhr.send(options.data || null);
            },
            abort: function () {
              jqXHR.abort();
            },
          };
        }
      }
    );
  }
}

$(window).on("unload", function () {
  unloading = true;
});

$(window).on("beforeunload", () => {
  unloadRequested = true;
  window.setTimeout(function () {
    // reset the unload requested flag, as the user could cancel the unload
    // (e.g. in the case of a dirty document which causes a Stay on this page? prompt.)
    unloadRequested = false;
  }, 2000);
});

function _getFailedRequestError(
  xhr: JQueryXHR,
  textStatus: string,
  errorThrown: any
) {
  //not a non authoritative response then handle it regular way
  var serverError: any, errorMessage: string, error: Error;

  if (!xhr) {
    error = new Error(
      Utils_String.format(
        Resources_Platform.WebApiUndefinedRequestError,
        0,
        errorThrown || ""
      )
    );
    error.name = "TFS.WebApi.Exception";
    return error;
  }

  if (textStatus !== "abort") {
    try {
      serverError = JSON.parse(xhr.responseText);
      if (serverError && serverError.message) {
        errorMessage = serverError.message;
      }
    } catch (exc) {}
  }

  if (!errorMessage) {
    if (xhr.status === 0) {
      errorMessage = Utils_String.format(
        Resources_Platform.WebApiUndefinedRequestError,
        xhr.status,
        textStatus
      );
    } else {
      errorMessage = xhr.status + ": " + textStatus;
    }
  }

  error = new Error(errorMessage);
  error.name = "TFS.WebApi.Exception";
  (<any>error).status = xhr.status;
  (<any>error).responseText = xhr.responseText;

  if (serverError) {
    (<any>error).serverError = serverError;
  }

  if (errorThrown == "timeout") {
    error.name = "TFS.WebApi.Exception.Timeout";
  }

  return error;
}

interface IRequestFailureHandler {
  (
    jqXHR: JQueryXHR,
    textStatus: string,
    errorThrown: string,
    requestId: number,
    deferred: Q.Deferred<any>
  ): void;
}

function _handleRequestFailure(
  jqXHR: JQueryXHR,
  textStatus: string,
  errorThrown: string,
  requestId: number,
  deferred: Q.Deferred<any>,
  handler: IRequestFailureHandler
) {
  // chrome will invoke .fail when the browser refreshes or navigates away
  //  and it happens before the onbeforeunload event
  // in this case, jqXHR.status is 0, jqXHR.readyState is 0,
  // jqXHR.getAllResponseHeaders() is "", jqXHR.statusText is "error",
  // textStatus is "error", errorThrown is ""
  if (jqXHR && jqXHR.status === 0) {
    if (!unloadRequested && !unloading) {
      // give the unload events a chance to fire
      Utils_Core.delay(this, 2000, () => {
        if (!unloadRequested && !unloading) {
          handler(jqXHR, textStatus, errorThrown, requestId, deferred);
        }
      });
    }
  } else {
    handler(jqXHR, textStatus, errorThrown, requestId, deferred);
  }
}

function _issueJQueryAjaxRequest(
  requestUrl: string,
  ajaxOptions: JQueryAjaxSettings,
  vssRequestOptions: IVssAjaxOptions,
  retryDeferred?: Q.Deferred<any>
): IPromise<any> {
  var deferred = retryDeferred || Q.defer();

  // Ensure custom transports are registered
  CustomTransports.register();

  // Add a X-VSS-ReauthenticationAction header to all AJAX requests
  if (!ajaxOptions) {
    ajaxOptions = {};
  }
  if (!ajaxOptions.headers) {
    ajaxOptions.headers = {};
  }
  ajaxOptions.headers["X-VSS-ReauthenticationAction"] = "Suppress";

  // Add current sessionId and command for correlation
  let sessionId: string, command: string;
  if (vssRequestOptions) {
    sessionId = vssRequestOptions.sessionId;
    command = vssRequestOptions.command;
  }
  if (!sessionId) {
    const pageContext = Context.getPageContext();
    if (
      pageContext &&
      pageContext.diagnostics &&
      pageContext.diagnostics.sessionId
    ) {
      sessionId = pageContext.diagnostics.sessionId;
    }
  }
  if (sessionId) {
    if (command) {
      ajaxOptions.headers["X-TFS-Session"] = sessionId + "," + command;
    } else {
      ajaxOptions.headers["X-TFS-Session"] = sessionId;
    }
  }

  // Include the request-scoped fault injection cookie
  const cookieValue = Utils_Core.getCookie("AzDevRequestFaults");
  if (cookieValue) {
    ajaxOptions.headers["AzDevRequestFaults"] = cookieValue;
  }

  if (vssRequestOptions && vssRequestOptions.authTokenManager) {
    // Token manager exists, we need to include auth token in the request header
    var retry = !!retryDeferred;
    var authTokenPromise =
      vssRequestOptions.authTokenManager.getAuthToken(retry);

    // Get session token before making the call
    authTokenPromise.then((sessionToken: any) => {
      if (sessionToken) {
        // Attach to before send event to modify request header
        ajaxOptions = $.extend(ajaxOptions, {
          beforeSend: (request: XMLHttpRequest) => {
            // Set auhorization header
            var authHeader =
              vssRequestOptions.authTokenManager.getAuthorizationHeader(
                sessionToken
              );
            request.setRequestHeader("Authorization", authHeader);
            request.setRequestHeader("X-TFS-FedAuthRedirect", "Suppress");

            const providerName = vssRequestOptions.authTokenManager.providerName;
            if (providerName) {
                request.setRequestHeader("X-VSS-ClientAuthProvider", providerName);
            }
          },
        });

        // Perform ajax call
        jQuery.ajax(requestUrl, ajaxOptions).then(
          (data: any, textStatus: string, jqXHR: JQueryXHR) => {
            deferred.resolve(<JQueryAjaxSuccessResult>{
              data: data,
              jqXHR: jqXHR,
              textStatus: textStatus,
            });
          },
          (jqXHR: JQueryXHR, textStatus: string, errorThrown: any) => {
            if (jqXHR && jqXHR.status === 401 && !retry) {
              // Unauthorized
              // Specifying existing deferred will cause to issue a new token
              _issueJQueryAjaxRequest(
                requestUrl,
                ajaxOptions,
                vssRequestOptions,
                deferred
              );
            } else {
              // If other error or 401 after retry, reject the request
              deferred.reject(<JQueryAjaxErrorResult>{
                errorThrown: errorThrown,
                jqXHR: jqXHR,
                textStatus: textStatus,
              });
            }
          }
        );
      } else {
        // Make sure adding X-TFS-FedAuthRedirect header to prevent redirecting to login page
        ajaxOptions = $.extend(ajaxOptions, {
          beforeSend: (request: XMLHttpRequest) => {
            request.setRequestHeader("X-TFS-FedAuthRedirect", "Suppress");
          },
        });

        // No token meaning user is anonymous or public, perform request without auth header
        _issueAjaxRequestWithoutAuthHeader(requestUrl, ajaxOptions, deferred);
      }
    }, deferred.reject);
  } else {
    _issueAjaxRequestWithoutAuthHeader(requestUrl, ajaxOptions, deferred);
  }

  return deferred.promise;
}

function _issueAjaxRequestWithoutAuthHeader(
  requestUrl: string,
  ajaxOptions: JQueryAjaxSettings,
  deferred: Q.Deferred<any>
): void {
  // JQuery doesn't tell us the URL the request got redirected to, but it lets us specify the XHR for it to use, and
  // we can get the response URL from it.
  let xhr: XMLHttpRequest | undefined;
  if (!ajaxOptions.xhr) {
    xhr = new XMLHttpRequest();
    ajaxOptions.xhr = () => xhr;
  }

  jQuery.ajax(requestUrl, ajaxOptions).then(
    (data: any, textStatus: string, jqXHR: JQueryXHR) => {
      if (jqXHR) {
        (jqXHR as any).xhr = xhr;
      }
      deferred.resolve(<JQueryAjaxSuccessResult>{
        data: data,
        jqXHR: jqXHR,
        textStatus: textStatus,
      });
    },
    (jqXHR: JQueryXHR, textStatus: string, errorThrown: any) => {
      if (jqXHR) {
        (jqXHR as any).xhr = xhr;
      }
      deferred.reject(<JQueryAjaxErrorResult>{
        errorThrown: errorThrown,
        jqXHR: jqXHR,
        textStatus: textStatus,
      });
    }
  );
}

/**
 * Determines how a RestClient should respond to an error.
 *
 * Keep in sync with new web platform declaration in VSS/Platform/IPlatformRestErrorHandler
 */
const enum RestErrorHandlerResponse {
  /**
   * Retry the request
   */
  Retry = 1,
  /**
   * Continue with the error handling process
   */
  Continue = 2,
}

function _issueRequest(
  requestUrl: string,
  ajaxOptions: JQueryAjaxSettings,
  vssRequestOptions?: IVssAjaxOptions
): IPromise<any> {
  var deferred = Q.defer<any>();
  var requestId = ++globalRequestId;
  var useAjaxResult = vssRequestOptions && vssRequestOptions.useAjaxResult;

  $.each(eventListeners, (i: number, listener: IVssAjaxEventListener) => {
    if (listener.beforeRequest) {
      listener.beforeRequest.call(
        this,
        requestId,
        requestUrl,
        ajaxOptions,
        vssRequestOptions
      );
    }
  });

  _issueJQueryAjaxRequest(requestUrl, ajaxOptions, vssRequestOptions).then(
    (result: JQueryAjaxSuccessResult) => {
      $.each(eventListeners, (i: number, listener: IVssAjaxEventListener) => {
        if (listener.responseReceived) {
          listener.responseReceived.call(
            this,
            requestId,
            result.data,
            result.textStatus,
            result.jqXHR,
            vssRequestOptions
          );
        }
      });

      try {
        if (useAjaxResult === true) {
          // We don't want xhr object to be thennable since q will resolve it.
          // In this case, we need xhr object itself.
          delete result.jqXHR.then;
          deferred.resolve([result.data, result.textStatus, result.jqXHR]);
        } else {
          deferred.resolve(result.data);
        }

        deferred.resolve(
          useAjaxResult === true
            ? [result.data, result.textStatus, result.jqXHR]
            : result.data
        );
      } finally {
        $.each(eventListeners, (i: number, listener: IVssAjaxEventListener) => {
          if (listener.postResponseCallback) {
            listener.postResponseCallback.call(
              this,
              requestId,
              result.data,
              result.textStatus,
              result.jqXHR,
              vssRequestOptions
            );
          }
        });
      }
    },
    (result: JQueryAjaxErrorResult) => {
      _handleRequestFailure(
        result.jqXHR,
        result.textStatus,
        result.errorThrown,
        requestId,
        deferred,
        (
          jqXHR: JQueryXHR,
          textStatus: string,
          errorThrown: string,
          requestId: number,
          deferred: Q.Deferred<any>
        ) => {
          var error = _getFailedRequestError(jqXHR, textStatus, errorThrown);
          (error as any).jqXHR = jqXHR;
          $.each(
            eventListeners,
            (i: number, listener: IVssAjaxEventListener) => {
              if (listener.responseReceived) {
                listener.responseReceived.call(
                  this,
                  requestId,
                  error,
                  textStatus,
                  jqXHR,
                  vssRequestOptions
                );
              }
            }
          );

          try {
            deferred.reject(error);
          } finally {
            $.each(
              eventListeners,
              (i: number, listener: IVssAjaxEventListener) => {
                if (listener.postResponseCallback) {
                  listener.postResponseCallback.call(
                    this,
                    requestId,
                    error,
                    textStatus,
                    jqXHR,
                    vssRequestOptions
                  );
                }
              }
            );
          }
        }
      );
    }
  );

  return deferred.promise;
}

/**
 * Issue an AJAX request. This is a wrapper around jquery's ajax method that handles VSS authentication
 * and triggers events that can be listened to by other modules.
 *
 * @param requestUrl Url to send the request to
 * @param ajaxOptions jQuery.ajax options
 * @param vssRequestOptions VSS specific request options
 * @param useAjaxResult If true, textStatus and jqXHR are added to the success callback. In this case, spread (instead of then) needs to be used
 */
export function issueRequest(
  requestUrl: string,
  ajaxOptions: JQueryAjaxSettings,
  vssRequestOptions?: IVssAjaxOptions
): IPromise<any> {
  return _issueRequest(requestUrl, ajaxOptions, vssRequestOptions);
}

/**
 * Add a listener that gets notified whenever requests from this client begin/end/etc.
 *
 * @param listener HttpClient listener to add
 */
export function addGlobalListener(listener: IVssAjaxEventListener) {
  eventListeners.push(listener);
}

/**
 * Remove a listener that gets notified whenever requests from this client begin/end/etc.
 *
 * @param listener HttpClient listener to remove
 */
export function removeGlobalListener(listener: IVssAjaxEventListener) {
  eventListeners = $.grep(eventListeners, (l: IVssAjaxEventListener) => {
    return l !== listener;
  });
}

//
// Add default global listener for tracking Ajax event progress
//
addGlobalListener({
  beforeRequest: (
    requestId: number,
    requestUrl: string,
    ajaxOptions: JQueryAjaxSettings,
    vssRequestOptions?: IVssAjaxOptions
  ) => {
    var actionId;
    var showProgressIndicator =
      !vssRequestOptions || vssRequestOptions.showProgressIndicator !== false;

    if (showProgressIndicator) {
      actionId = VSS.globalProgressIndicator.actionStarted(
        "http " + requestId + " " + requestUrl
      );
      outstandingRequestActionIds[requestId] = actionId;
    }
    Diag.logTracePoint("Ajax.request-started", actionId);
  },
  responseReceived: (
    requestId: number,
    data: any,
    textStatus: string,
    jqXHR: JQueryXHR
  ) => {
    if (requestId in outstandingRequestActionIds) {
      VSS.globalProgressIndicator.actionCompleted(
        outstandingRequestActionIds[requestId]
      );
    }
  },
  postResponseCallback: (
    requestId: number,
    data: any,
    textStatus: string,
    jqXHR: JQueryXHR
  ) => {
    var actionId: Number;
    if (requestId in outstandingRequestActionIds) {
      actionId = outstandingRequestActionIds[requestId];
      delete outstandingRequestActionIds[requestId];
    }
    Diag.logTracePoint("Ajax.callback-complete", actionId);
  },
});

//
// Add default global listener for handling global message
//
addGlobalListener({
  responseReceived: (
    requestId: number,
    data: any,
    textStatus: string,
    jqXHR: JQueryXHR
  ) => {
    if (jqXHR && jqXHR.getResponseHeader) {
      const globalMessageWarningHeader = jqXHR.getResponseHeader(
        "X-VSS-GlobalMessage"
      );
      if (globalMessageWarningHeader) {
        try {
          const banner = JSON.parse(globalMessageWarningHeader);
          let message = banner.message;
          if (banner.messageFormat && banner.messageLinks) {
            message = Utils_String.format(
              banner.messageFormat,
              ...banner.messageLinks.map((link) => {
                return $("<span/>")
                  .append(
                    $("<a/>")
                      .text(link.name || "")
                      .attr("href", link.href || "")
                      .attr("target", "_blank")
                  )
                  .html();
              })
            );
          }
          VSS.globalMessageIndicator.updateGlobalMessageIfEmpty(message);
          if ((window as any).LWL) {
            document.body.dispatchEvent(
              new CustomEvent("legacyGlobalMessage", { detail: banner })
            );
          }
        } catch (ex) {
          console.warn(
            "Failed to deserialize X-VSS-GlobalMessage header: " + ex
          );
        }
      }
    }
  },
});
